/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { MutationKey, useQueryClient } from '@tanstack/react-query';
import { ArrayHelper } from '@zz2/zz2-ui';
import moment from 'moment';
import { IUser } from '../../@types/model/right/user/user';
import { IUserToken } from '../../@types/model/right/userToken/userToken';
import { useAppDispatch, useAppSelector } from '../../@types/redux';
import { setLocalStorageSession } from '../../service/localStorageService';
import AuthActions from '../../store/auth/actions';
import GeneralThunks from '../../store/general/thunks';
import lodash from 'lodash';

export const queryErrorHandler = (errorMessage : string) => {
    const dispatch = useAppDispatch();

    const handleError = (error : Error) => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: errorMessage,
            e: error,
        }));
    };
    
    return handleError;
};

export const querySuccessHandler = <T extends { id : number }>(getKey : MutationKey, successMessage : string, getKeyFilterId ?: string) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const handleOnSuccess = (result : T | Array<T>) => {
        const isArrayType = Array.isArray(result);

        let queryKey = getKey;

        if (getKeyFilterId) {
            const dataModel = isArrayType ? result[0] : result;

            if (getKeyFilterId in dataModel) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                queryKey = [getKey, (dataModel as any)[getKeyFilterId]];
            }
        }

        queryClient.setQueryData(queryKey, (cachedData ?: Array<T>) => {
            if (!cachedData) return [result];


            if (isArrayType) {
                return ArrayHelper.upsertElements(cachedData, [...result], (a : T, b : T) => a.id == b.id);
            } else {
                return ArrayHelper.upsertElement(cachedData, result, (x => x.id === result.id));
            }
        });

        dispatch(GeneralThunks.showSuccessSnackbar(successMessage));
    };

    return handleOnSuccess;
};

export const queryDeleteSuccessHandler = <T extends { id : number; isActive : boolean; updatedOn : moment.Moment }>(getKey : MutationKey, successMessage : string) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const handleOnSuccess = (data : void, deletedId : number) => {
        queryClient.setQueriesData(getKey, (cachedData ?: Array<T>) => {
            if (!cachedData) return [cachedData];

            const deletedData = lodash.cloneDeep(cachedData.find(x => x.id === deletedId));

            if (deletedData) {
                deletedData.isActive = false;
                // deletedData.updatedOn = moment();
                return ArrayHelper.upsertElement(cachedData, deletedData, (x => x.id === deletedId));
            }
            
            return;
        });

        dispatch(GeneralThunks.showSuccessSnackbar(successMessage));
    };

    return handleOnSuccess;
};

export const queryNoReturnContentSuccessHandler = (successMessage : string) => {
    const dispatch = useAppDispatch();
    
    const handleOnSuccess = () => {
        dispatch(GeneralThunks.showSuccessSnackbar(successMessage));
    };

    return handleOnSuccess;
};

export const queryUserUpsertSuccessHandler = (getKey : MutationKey, successMessage : string, getKeyFilterId ?: string) : (result : Array<IUser>) => void => {
    const dispatch = useAppDispatch();
    const session = useAppSelector<IUserToken | null>(x => x.auth.session);
    const queryClient = useQueryClient();

    const handleOnSuccess = async (result : Array<IUser>) : Promise<void> => {
        let queryKey = getKey;

        if (getKeyFilterId) {
            const dataModel = result;

            if (getKeyFilterId in dataModel) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                queryKey = [getKey, (result as any)[getKeyFilterId]];
            }
        }
        
        queryClient.setQueryData(queryKey, (cachedData ?: Array<IUser>) => {
            if (!cachedData) return [result];

            return ArrayHelper.upsertElements(cachedData, [...result], (a : IUser, b : IUser) => a.id == b.id);
        });

        if (session) {
            const updatedCurrentUser = result.find(x => x.id == session.userId);
            if (updatedCurrentUser) {
                const updatedSession = {
                    ...session,
                    user: updatedCurrentUser,
                };
                
                dispatch(AuthActions.setSession(updatedSession));
                await setLocalStorageSession(updatedSession);
            }
        }

        dispatch(GeneralThunks.showSuccessSnackbar(successMessage));
    };

    return handleOnSuccess;
};

export const queryUpdateSessionSuccessHandler = (successMessage : string) => {
    const dispatch = useAppDispatch();
    const session = useAppSelector<IUserToken | null>(x => x.auth.session);

    const handleOnSuccess = async (data : IUser) => {
        if (!session) return;

        const updatedSession = {
            ...session,
            user: data,
        };

        dispatch(AuthActions.setSession(updatedSession));
        await setLocalStorageSession(updatedSession);
        
        dispatch(GeneralThunks.showSuccessSnackbar(successMessage));
    };

    return handleOnSuccess;
};